import { Phone, MessageSquare, Mail, FileText, Calendar, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CommunicationFilters as FilterType, CommunicationType, CommunicationDirection } from '@/types/communications';

interface CommunicationFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
}

const typeOptions: { value: CommunicationType | 'all'; label: string; icon: React.ElementType }[] = [
  { value: 'all', label: 'All Types', icon: Phone },
  { value: 'call', label: 'Calls', icon: Phone },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'note', label: 'Notes', icon: FileText },
  { value: 'meeting', label: 'Meetings', icon: Calendar },
];

const dateRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
];

const directionOptions: { value: CommunicationDirection | 'all'; label: string }[] = [
  { value: 'all', label: 'All Directions' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
];

export function CommunicationFilters({ filters, onFiltersChange }: CommunicationFiltersProps) {
  const updateFilter = <K extends keyof FilterType>(key: K, value: FilterType[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Type Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {typeOptions.map((option) => {
          const Icon = option.icon;
          const isActive = filters.type === option.value;
          return (
            <Button
              key={option.value}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFilter('type', option.value)}
              className="h-8 gap-1 rounded-full text-xs"
            >
              <Icon className="h-3.5 w-3.5" />
              {option.label}
            </Button>
          );
        })}
      </div>

      {/* Secondary Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range */}
        <Select
          value={filters.dateRange}
          onValueChange={(value) => updateFilter('dateRange', value as FilterType['dateRange'])}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            {dateRangeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Direction */}
        <Select
          value={filters.direction}
          onValueChange={(value) => updateFilter('direction', value as FilterType['direction'])}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            {directionOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, or content..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
    </div>
  );
}
